import { Fragment, memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Marker } from "react-mapbox-gl";

import {
  COLLECTIONS_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
} from "../../../consts/planTypes";

import {
  getMatrixProfileType,
  heatmapViewSelector,
  routesSelector,
  multiRoutesPopupLocationSelector,
  visibleLabelsSelector,
} from "../../../reducers/matrixProfile/selectors";
import { selectedRouteSelector } from "../../../reducers/ownApp/selectors";
import { zoomLevelSelector } from "../../../reducers/map/selectors";
import { setZoomFromMouseEvent } from "../../../reducers/map/thunkActions";

import { CollectionOverridesMarker } from "./CollectionOverridesMarker";
import MultiLabelLayer from "../MultiLabelLayer";
import { RouteMarker } from "./RouteMarker";
import { VolumeMarker } from "./VolumeMarker";
import { OverrideMarkerLabel } from "./OverrideMarkerLabel";
import { OverrideMarkerPopup } from "./OverrideMarkerPopup";
import { PickupMarkerPopup } from "./PickupMarkerPopup";
import { PickupMarkerLabel } from "./PickupMarkerLabel";

const useStyles = makeStyles({
  marker: {
    paddingBottom: "8px",
    "& *": {
      // Markers not selectable
      "user-select": "none" /* supported by Chrome and Opera */,
      "-webkit-user-select": "none" /* Safari */,
      "-khtml-user-select": "none" /* Konqueror HTML */,
      "-moz-user-select": "none" /* Firefox */,
      "-ms-user-select": "none" /* Internet Explorer/Edge */,
    },
  },
});

export const RouteLabelLayer = memo(() => {
  const visibleLabels = useSelector(visibleLabelsSelector);
  const isHeatmapOn = useSelector(heatmapViewSelector);
  const planType = useSelector(getMatrixProfileType);
  const styles = useStyles();
  const [selectedMarker, setSelectedMarker] = useState();
  const multiRoutesPopupLocation = useSelector(
    multiRoutesPopupLocationSelector
  );
  const selectedRoute = useSelector(selectedRouteSelector);
  const dispatch = useDispatch();
  const zoomLevel = useSelector(zoomLevelSelector);
  const routes = useSelector(routesSelector);
  const [overrideModalsToShow, setOverrideModalsToShow] = useState([]); // array of coordinates
  const [pickupModalsToShow, setPickupModalsToShow] = useState({}); // map of postcodes

  useEffect(() => {
    setSelectedMarker(multiRoutesPopupLocation);
  }, [multiRoutesPopupLocation]);

  useEffect(() => {
    setSelectedMarker(null);
  }, [zoomLevel]);

  const handlePickupModalChange = (toAdd, { postcode, onClose }) => {
    const newPickupModalsToShow = { ...pickupModalsToShow };
    if (toAdd) newPickupModalsToShow[postcode] = { onClose };
    else delete newPickupModalsToShow[postcode];

    setPickupModalsToShow(newPickupModalsToShow);
  };

  const handleOverrideModalChange = (toAdd, { coordinates, onClose }) => {
    if (toAdd) {
      setOverrideModalsToShow([
        ...overrideModalsToShow,
        {
          coordinates,
          onClose,
        },
      ]);
    } else {
      const filteredModalsToShow = overrideModalsToShow.filter(
        ({ coordinates: [lat, long] }) =>
          lat !== coordinates[0] && long !== coordinates[1]
      );
      setOverrideModalsToShow(filteredModalsToShow);
    }
  };

  if (planType === LOCATIONS_PLAN_TYPE) return null;
  return (
    <>
      {visibleLabels &&
        visibleLabels.map((feature, index) => {
          return (
            <Fragment key={index}>
              <Marker
                coordinates={feature.geometry.coordinates}
                anchor={"bottom"}
                className={styles.marker}
                onClick={() => {
                  setSelectedMarker(feature);
                }}
                onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
              >
                <Grid
                  item
                  container
                  alignItems={
                    planType === COLLECTIONS_PLAN_TYPE
                      ? "flex-end"
                      : "flex-start"
                  }
                  direction={
                    planType === COLLECTIONS_PLAN_TYPE ? "row-reverse" : "row"
                  }
                >
                  {feature.properties && (
                    <RouteMarker
                      routes={[feature.properties.route]}
                      postcode={feature.properties.postcode}
                      individual={feature.properties.individual}
                      leftMargin={planType === COLLECTIONS_PLAN_TYPE}
                    />
                  )}
                  {feature.properties && isHeatmapOn && (
                    <VolumeMarker postcode={feature.properties.postcode} />
                  )}
                  {planType === COLLECTIONS_PLAN_TYPE && (
                    <CollectionOverridesMarker
                      collection={feature.collection}
                      mismatches={feature.mismatches}
                    />
                  )}
                  {planType === PICKUP_PLAN_TYPE && (
                    <PickupMarkerLabel
                      postcode={feature.postcode}
                      pickupConfig={feature.pickupConfig}
                      handlePickupModalChange={handlePickupModalChange}
                    />
                  )}
                  {planType === OVERRIDES_PLAN_TYPE && (
                    <>
                      <OverrideMarkerLabel
                        routes={routes}
                        selectedRoute={selectedRoute}
                        overrides={feature.override}
                        coordinates={feature.geometry.coordinates}
                        handleOverrideModalChange={handleOverrideModalChange}
                      />
                    </>
                  )}
                </Grid>
              </Marker>
              {feature.override && (
                <OverrideMarkerPopup
                  routes={routes}
                  overrides={feature.override}
                  coordinates={feature.geometry.coordinates}
                  overrideModalsToShow={overrideModalsToShow}
                  handleOverrideModalChange={handleOverrideModalChange}
                />
              )}
              {feature.pickupConfig && pickupModalsToShow[feature.postcode] && (
                <PickupMarkerPopup
                  postcode={feature.postcode}
                  pickupConfig={feature.pickupConfig}
                  coordinates={feature.geometry.coordinates}
                  handlePickupModalChange={handlePickupModalChange}
                  onClose={pickupModalsToShow[feature.postcode].onClose}
                />
              )}
            </Fragment>
          );
        })}
      <MultiLabelLayer />
      {!!selectedMarker &&
        (selectedMarker.coordinates ||
          selectedMarker.geometry?.coordinates) && (
          <Marker
            coordinates={
              selectedMarker.coordinates || selectedMarker.geometry.coordinates
            }
            anchor={"bottom"}
            className={styles.marker}
            onClick={() => setSelectedMarker(selectedMarker)}
            onWheel={(e) => dispatch(setZoomFromMouseEvent(e))}
          >
            <Grid
              item
              container
              alignItems={
                planType === COLLECTIONS_PLAN_TYPE ? "flex-end" : "flex-start"
              }
              direction={
                planType === COLLECTIONS_PLAN_TYPE ? "row-reverse" : "row"
              }
            >
              {(selectedMarker.properties || selectedMarker.routes) && (
                <RouteMarker
                  routes={
                    selectedMarker.routes || [selectedMarker.properties.route]
                  }
                  postcode={
                    selectedMarker.properties?.postcode ||
                    selectedMarker.postcode
                  }
                  individual={selectedMarker.properties?.individual}
                  leftMargin={planType === COLLECTIONS_PLAN_TYPE}
                />
              )}
              {isHeatmapOn && planType !== COLLECTIONS_PLAN_TYPE && (
                <VolumeMarker
                  postcode={
                    selectedMarker.postcode ||
                    selectedMarker.properties.postcode
                  }
                />
              )}
              {planType === COLLECTIONS_PLAN_TYPE && (
                <CollectionOverridesMarker
                  collection={selectedMarker.collection}
                  mismatches={selectedMarker.mismatches}
                />
              )}
            </Grid>
          </Marker>
        )}
    </>
  );
});
